import { Fragment, useCallback, useEffect, useState } from 'react'
import { adDetectionApi } from '../../api'
import { useAuth } from '../../AuthContext'
import { ErrorState, errorMessage } from '../../components/ErrorState'
import type { AdEvolutionPoint, AdScanHistoryRow, AdSearchQuery, PaginatedResponse, ScanLogEntry } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { navigate } from '../../shell/router'
import { isManagerRole } from '../../shell/navigation'
import { Block, CollapsibleBlock, PageHeader, StatusPill } from '../../ui/ui'
import { useI18n } from '../../i18n'
import { useAds } from './AdsContext'
import { GOOGLE_PAGES_PER_QUERY, formatDuration } from './adsShared'
import { countryName, inCountry } from './AdsModule'

/** Bandeau commun aux écrans pubs : scan en cours ou résultat du dernier scan. */
export function ScanStatusBanner({ showLink = true }: { showLink?: boolean }) {
  const { t } = useI18n()
  const { running, runningParts, runningCountry, aborting, progress, result, clearResult, abort } = useAds()

  if (running) {
    const index = (progress?.currentSiteIndex ?? 0) + 1
    const total = progress?.totalSites ?? 0
    // La collecte des annonceurs finit en quelques minutes, le passage sur les
    // sites surveillés peut durer une heure. Dire lequel tourne encore évite de
    // lire un scan bloqué quand l'historique annonce déjà « Terminé ».
    const titre = aborting
      ? t('ads.bannerAbortRequested')
      : !runningParts.ads && runningParts.banners
        ? t('ads.bannerSitesRunning')
        : t('ads.bannerScanRunning')
    const attente =
      !runningParts.ads && runningParts.banners
        ? t('ads.bannerWaitSites')
        : t('ads.bannerWaitAll')
    return (
      <div className="ads-scan-banner" role="status">
        <span className="scan-spinner" aria-hidden="true" />
        <div className="ads-scan-banner-text">
          <strong>{titre}{runningCountry ? ` ${inCountry(t, runningCountry)}` : ''}</strong>
          <span>
            {aborting
              ? t('ads.bannerAborting', { site: progress?.currentSite ? ` — ${progress.currentSite}` : '' })
              : progress?.type === 'site-start' && progress.currentSite
                ? t('ads.bannerSiteStart', { index, total, site: progress.currentSite })
                : progress?.type === 'site-done' && progress.currentSite
                  ? t('ads.bannerSiteDone', {
                      index,
                      total,
                      site: progress.currentSite,
                      outcome: progress.error
                        ? t('ads.bannerSiteError')
                        : t('ads.bannerSiteBanners', { n: progress.bannersFound ?? 0 }),
                    })
                  : attente}
          </span>
          {total > 0 && (
            <span className="ads-progress" aria-hidden="true">
              <i style={{ width: `${Math.min(100, (index / total) * 100)}%` }} />
            </span>
          )}
        </div>
        {showLink ? (
          <button type="button" className="block-link" onClick={() => navigate('ads/campaigns')}>{t('ads.followScan')}</button>
        ) : (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={abort}
            disabled={aborting}
            title={aborting ? t('ads.stopAlready') : t('ads.stopHint')}
          >
            {aborting ? t('ads.stopping') : t('ads.stop')}
          </button>
        )}
      </div>
    )
  }

  if (result) {
    return (
      <div className={`ads-scan-banner ${result.isError ? 'ads-scan-banner-error' : 'ads-scan-banner-ok'}`} role="status">
        <div className="ads-scan-banner-text">
          <strong>{result.message}</strong>
        </div>
        <button type="button" className="block-link" onClick={clearResult}>{t('common.hide')}</button>
      </div>
    )
  }
  return null
}

export function AdsCampaigns() {
  const { user } = useAuth()
  const { t, fmt } = useI18n()
  const { country } = useShell()
  const { countries, running, runningParts, startScan, logs, refreshKey, config } = useAds()
  const canManage = isManagerRole(user)
  const [scanCountry, setScanCountry] = useState(country)
  const [pages, setPages] = useState(0)
  const [quota, setQuota] = useState<Awaited<ReturnType<typeof adDetectionApi.getSerpApiQuota>> | null | 'unavailable'>(null)
  const [queries, setQueries] = useState<AdSearchQuery[]>([])
  const [history, setHistory] = useState<PaginatedResponse<AdScanHistoryRow> | null>(null)
  const [evolution, setEvolution] = useState<AdEvolutionPoint[]>([])
  const [historyPage, setHistoryPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => setScanCountry(country), [country])
  useEffect(() => setHistoryPage(1), [country])

  const load = useCallback(() => {
    setError(null)
    Promise.all([
      adDetectionApi.getHistory({ country: country || undefined, page: historyPage, limit: 15 }),
      adDetectionApi.getEvolution(country || undefined, 90),
    ])
      .then(([h, e]) => {
        setHistory(h)
        setEvolution(e)
      })
      .catch((err) => setError(errorMessage(err)))
  }, [country, historyPage])

  useEffect(load, [load, refreshKey])

  useEffect(() => {
    adDetectionApi.getSerpApiQuota().then(setQuota).catch(() => setQuota('unavailable'))
    adDetectionApi.getQueries().then(setQueries).catch(() => setQueries([]))
  }, [refreshKey])

  const activeForCountry = queries.filter((q) => q.isActive && (!q.countries?.length || !scanCountry || q.countries.includes(scanCountry))).length
  const estimatedSearches = activeForCountry * GOOGLE_PAGES_PER_QUERY

  const reset = async () => {
    if (!scanCountry) return
    if (!window.confirm(t('ads.resetConfirm', { where: inCountry(t, scanCountry) }))) return
    setNotice(null)
    try {
      const res = await adDetectionApi.resetCountry(scanCountry)
      setNotice(
        t('ads.resetDone', {
          country: countryName(t, scanCountry),
          advertisers: res.advertisersDeleted,
          history: res.historyDeleted,
          detections: res.detectionsDeleted,
        }),
      )
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const maxFound = Math.max(1, ...evolution.map((p) => p.newAdvertisers))

  return (
    <>
      <PageHeader title={t('ads.campaignsTitle')} description={t('ads.campaignsDesc')} />

      <ScanStatusBanner showLink={false} />
      {notice && <div className="ads-scan-banner ads-scan-banner-ok" role="status"><div className="ads-scan-banner-text"><strong>{notice}</strong></div></div>}

      <div className="grid-main-side">
        <Block title={t('ads.launchBlock')} subtitle={config && !config.googleAdsConfigured ? t('ads.googleNotConfigured') : undefined}>
          <div className="form-stack ads-scan-form">
            <div className="bet-form-row">
              <label>
                {t('common.country')}
                <select value={scanCountry} onChange={(e) => setScanCountry(e.target.value)} disabled={running}>
                  <option value="">{t('ads.pickCountry')}</option>
                  {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </label>
              <label>
                {t('ads.internalPages')}
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={pages}
                  disabled={running}
                  onChange={(e) => setPages(Math.min(20, Math.max(0, Number(e.target.value) || 0)))}
                />
              </label>
            </div>
            <p className="field-note">
              {scanCountry
                ? t('ads.costNote', {
                    n: activeForCountry,
                    where: inCountry(t, scanCountry),
                    searches: estimatedSearches,
                  })
                : t('ads.costNoteNoCountry')}
            </p>
            <div className="bet-form-actions ads-scan-actions">
              {canManage && scanCountry && !running && (
                <button type="button" className="block-link ads-danger-link" onClick={reset}>
                  {t('ads.clearResults', { where: inCountry(t, scanCountry) })}
                </button>
              )}
              <button type="button" className="btn btn-primary" disabled={running || !scanCountry} onClick={() => startScan(scanCountry, pages)}>
                {!running ? t('ads.runScan') : !runningParts.ads && runningParts.banners ? t('ads.sitesRunning') : t('ads.scanRunning')}
              </button>
            </div>
          </div>
        </Block>

        <Block title={t('ads.quotaBlock')}>
          {quota === null ? (
            <div className="stat-skeleton" style={{ minHeight: 80 }} />
          ) : quota === 'unavailable' ? (
            <p className="empty-note">{t('ads.quotaAdminOnly')}</p>
          ) : !quota.configured ? (
            <p className="empty-note">{t('ads.serpapiOff')}</p>
          ) : (
            <div className="ads-quota">
              <div className="ads-quota-figure">
                <span className="kpi-value">{fmt.number(quota.callsMade)}</span>
                <span className="block-sub">{t('ads.quotaSinceBoot')}</span>
              </div>
              <StatusPill tone={quota.exhausted ? 'crit' : 'ok'}>
                {quota.exhausted
                  ? quota.exhaustedAt
                    ? t('ads.quotaExhaustedSince', { date: fmt.dateTime(quota.exhaustedAt) })
                    : t('ads.quotaExhausted')
                  : t('ads.quotaAvailable')}
              </StatusPill>
              {Object.keys(quota.callsByCaller).length > 0 && (
                <ul className="ads-quota-callers">
                  {Object.entries(quota.callsByCaller)
                    .sort((x, y) => y[1] - x[1])
                    .map(([caller, n]) => (
                      <li key={caller}><span>{caller}</span><strong className="num">{n}</strong></li>
                    ))}
                </ul>
              )}
              <p className="field-note">{t('ads.quotaMonthlyNote')}</p>
            </div>
          )}
        </Block>
      </div>

      {logs.length > 0 && (
        <CollapsibleBlock
          title={t('ads.logBlock')}
          subtitle={t('ads.logSummary', {
            n: logs.length,
            errors: logs.filter((l) => l.level === 'ERROR').length,
            warnings: logs.filter((l) => l.level === 'WARN').length,
          })}
        >
          <ScanLogList logs={logs} />
        </CollapsibleBlock>
      )}

      <Block
        title={country ? t('ads.trendTitleCountry', { country: countryName(t, country) }) : t('ads.trendTitle')}
        subtitle={t('ads.trendSub')}
      >
        {evolution.length === 0 ? (
          <p className="empty-note">{t('ads.trendEmpty')}</p>
        ) : (
          <div className="ads-trend" role="img" aria-label={t('ads.trendAria')}>
            {evolution.map((p, i) => (
              <div
                key={`${p.date}-${p.country}-${i}`}
                className="ads-trend-col"
                title={t('ads.trendPoint', {
                  date: fmt.date(p.date),
                  country: countryName(t, p.country),
                  fresh: p.newAdvertisers,
                  total: p.totalFound,
                })}
              >
                <i style={{ height: `${Math.max(3, (p.newAdvertisers / maxFound) * 100)}%` }} />
                {(i % Math.max(1, Math.ceil(evolution.length / 8)) === 0 || i === evolution.length - 1) && (
                  <span className="num">{fmt.date(p.date)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Block>

      {/* Seuls les scans d'annonceurs sont consignés ici. Le titre le dit, sans
          quoi un « Terminé » dans ce tableau contredit le passage sur les sites
          qui, lui, court encore. */}
      <Block
        flush
        title={t('ads.historyTitle')}
        subtitle={history ? t('ads.historySub', { n: history.total }) : undefined}
      >
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !history ? (
          <div className="stat-skeleton" style={{ minHeight: 160, margin: 16 }} />
        ) : history.data.length === 0 ? (
          <div className="table-empty">{t('ads.historyEmpty')}</div>
        ) : (
          <div className="table-scroll">
            <table className="bet-table bet-table-clickable">
              <thead>
                <tr>
                  <th scope="col">{t('common.date')}</th>
                  <th scope="col">{t('common.country')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col" className="num-col">Google</th>
                  <th scope="col" className="num-col">Meta</th>
                  <th scope="col" className="num-col">{t('ads.colBanners')}</th>
                  <th scope="col" className="num-col">{t('ads.colNew')}</th>
                  <th scope="col" className="num-col">{t('ads.colDuration')}</th>
                  <th scope="col">{t('ads.colRemark')}</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      tabIndex={0}
                      aria-expanded={expanded === row.id}
                      onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setExpanded(expanded === row.id ? null : row.id)
                      }}
                    >
                      <td className="num">{fmt.dateTime(row.createdAt)}</td>
                      <td>{countryName(t, row.country)}</td>
                      <td>
                        <StatusPill tone={row.status === 'SUCCESS' ? 'ok' : row.status === 'PARTIAL' ? 'warn' : 'crit'}>
                          {row.status === 'SUCCESS' ? t('ads.statusSuccess') : row.status === 'PARTIAL' ? t('ads.statusPartial') : t('ads.statusFailed')}
                        </StatusPill>
                      </td>
                      <td className="num num-col">{row.googleCount}</td>
                      <td className="num num-col">{row.metaCount}</td>
                      <td className="num num-col">{row.bannerCount ?? 0}</td>
                      <td className="num num-col">{row.newAdvertisers > 0 ? `+${row.newAdvertisers}` : '0'}</td>
                      <td className="num num-col">{formatDuration(row.durationMs)}</td>
                      <td className="ads-remark">{row.error ?? (row.scanLogs?.length ? t('ads.seeLog') : '—')}</td>
                    </tr>
                    {expanded === row.id && (
                      <tr className="ads-log-row">
                        <td colSpan={9}>
                          {row.scanLogs?.length ? <ScanLogList logs={row.scanLogs} /> : <p className="empty-note">{t('ads.noLogForScan')}</p>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {history && history.totalPages > 1 && (
          <div className="pagination">
            <button disabled={historyPage <= 1} onClick={() => setHistoryPage((p) => p - 1)}>← {t('common.previous')}</button>
            <span className="page-info">{t('common.page', { page: history.page, pages: history.totalPages })}</span>
            <button disabled={historyPage >= history.totalPages} onClick={() => setHistoryPage((p) => p + 1)}>{t('common.next')} →</button>
          </div>
        )}
      </Block>
    </>
  )
}

function ScanLogList({ logs }: { logs: ScanLogEntry[] }) {
  const { t } = useI18n()
  return (
    <ul className="ads-logs">
      {logs.map((log, i) => (
        <li key={i} className={`ads-log ads-log-${log.level.toLowerCase()}`}>
          <span className="ads-log-level">
            {log.level === 'OK' ? t('ads.logLevelOk') : log.level === 'WARN' ? t('ads.logLevelWarn') : t('ads.logLevelError')}
          </span>
          <span className="ads-log-site">{log.site !== '-' ? log.site : ''}</span>
          <span className="ads-log-message">
            {log.message}
            {log.detail && <small>{log.detail}</small>}
          </span>
        </li>
      ))}
    </ul>
  )
}
